<script lang="ts">
  /// 行アクションを ⋯ ドロップダウンに集約する汎用メニュー (shared/ui)。
  /// 判断は持たず、メニュー項目 (button.menu-item) は呼び出し側が children で供給する。
  /// 外側クリック / ESC / 項目クリックで閉じる。
  import type { Snippet } from 'svelte';

  interface Props {
    /// トグルボタンの aria-label。
    label?: string;
    /// 白背景 + 薄い枠線で、単独のツールボタンとして見せる。
    bordered?: boolean;
    /// 利用可能な操作が無いときに無効化する。
    disabled?: boolean;
    children: Snippet;
  }
  let { label = '操作', bordered = false, disabled = false, children }: Props = $props();

  let open = $state(false);
  let root = $state<HTMLElement | null>(null);
  const menuOpen = $derived(open && !disabled);

  $effect(() => {
    if (disabled) open = false;
  });

  function toggle(): void {
    if (!disabled) open = !open;
  }
  function close(): void {
    open = false;
  }
  function onWindowClick(ev: MouseEvent): void {
    if (menuOpen && root && !root.contains(ev.target as Node)) close();
  }
  function onKey(ev: KeyboardEvent): void {
    if (menuOpen && ev.key === 'Escape') close();
  }
</script>

<svelte:window onclick={onWindowClick} onkeydown={onKey} />

<div class="kebab" bind:this={root}>
  <button
    type="button"
    class="kebab-toggle"
    class:bordered
    aria-haspopup="menu"
    aria-expanded={menuOpen}
    aria-label={label}
    title={label}
    {disabled}
    onclick={toggle}
  >
    ⋯
  </button>
  {#if menuOpen}
    <!-- 項目クリックはバブリングして閉じる (項目の onclick が先に発火する)。 -->
    <div class="kebab-menu" role="menu" tabindex="-1" onclick={close} onkeydown={onKey}>
      {@render children()}
    </div>
  {/if}
</div>

<style>
  .kebab {
    position: relative;
    display: inline-block;
  }
  .kebab-toggle {
    background: none;
    border: 1px solid transparent;
    border-radius: 6px;
    font-size: 18px;
    line-height: 1;
    padding: 2px 8px;
    color: var(--muted, #6b7280);
    cursor: pointer;
  }
  .kebab-toggle.bordered {
    background: #ffffff;
    border-color: #d1d5db;
  }
  .kebab-toggle:hover:not(:disabled) {
    background: var(--hover, rgba(0, 0, 0, 0.04));
  }
  .kebab-toggle:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
  .kebab-menu {
    position: absolute;
    right: 0;
    top: calc(100% + 4px);
    z-index: 100;
    min-width: 168px;
    background: var(--panel, #ffffff);
    border: 1px solid var(--border, #e3e6eb);
    border-radius: var(--radius, 6px);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
    padding: 4px;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }
  /* 項目は呼び出し側の scope で描画されるため :global で整える。 */
  .kebab-menu :global(.menu-item) {
    display: block;
    width: 100%;
    text-align: left;
    background: none;
    border: none;
    border-radius: 4px;
    padding: 8px 10px;
    font-size: 13px;
    color: var(--text, #1f2933);
    cursor: pointer;
    white-space: nowrap;
  }
  .kebab-menu :global(.menu-item:hover:not(:disabled)) {
    background: var(--hover, rgba(0, 0, 0, 0.04));
  }
  .kebab-menu :global(.menu-item:disabled) {
    opacity: 0.4;
    cursor: not-allowed;
  }
  .kebab-menu :global(.menu-item.danger) {
    color: var(--danger, #c83232);
  }
</style>
